import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Search, CheckCircle, XCircle, Building, Package } from 'lucide-react';
import { DashboardMetrics as DashboardMetricsType } from '@/types/amazonIndexChecker';

interface DashboardMetricsProps {
  metrics: DashboardMetricsType;
  loading?: boolean;
}

const DashboardMetrics: React.FC<DashboardMetricsProps> = ({ metrics, loading = false }) => {
  // 5-tier percentage color system (higher = better)
  const getPercentageColor = (percentage: number) => {
    if (percentage >= 75) return 'text-green-600 dark:text-green-400';
    if (percentage >= 50) return 'text-lime-600 dark:text-lime-400';
    if (percentage >= 25) return 'text-yellow-600 dark:text-yellow-400';
    if (percentage >= 10) return 'text-orange-600 dark:text-orange-400';
    return 'text-red-600 dark:text-red-400';
  };

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gray-200 rounded"></div>
                <div className="space-y-2 flex-1">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-6 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const metricCards = [
    {
      title: 'Total Terms',
      value: metrics.totalTermsChecked.toLocaleString(),
      icon: Search,
      color: 'text-blue-600'
    },
    {
      title: 'Indexed',
      value: metrics.totalIndexed.toLocaleString(),
      icon: CheckCircle,
      color: 'text-green-600'
    },
    {
      title: 'Not Indexed',
      value: metrics.totalNotIndexed.toLocaleString(),
      icon: XCircle,
      color: 'text-red-600'
    },
    {
      title: '% Indexed',
      value: `${metrics.percentageIndexed}%`,
      icon: CheckCircle,
      color: getPercentageColor(metrics.percentageIndexed)
    },
    {
      title: 'Total ASINs',
      value: metrics.totalASINs.toLocaleString(),
      icon: Package,
      color: 'text-indigo-600'
    },
    {
      title: 'Companies',
      value: metrics.companiesCount.toLocaleString(),
      icon: Building,
      color: 'text-purple-600'
    }
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
      {metricCards.map((metric, index) => {
        const IconComponent = metric.icon;
        return (
          <Card key={index} className="hover:shadow-lg hover:shadow-blue-500/25 hover:scale-105 transition-all duration-300 hover:border-blue-300 dark:hover:border-blue-600 cursor-pointer">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <IconComponent className={`h-8 w-8 ${metric.color}`} />
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    {metric.title}
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {metric.value}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default DashboardMetrics;